const KEY = 'pintarplay_v1';

const DEFAULTS = {
  onboarded: false,
  profile: { nickname: '', language: 'ms', bilingual: false },
  settings: { voice: true, music: true, sfx: true, sessionMinutes: 10, missions: true, musicVolume: 0.35 },
  progress: { stars: 0, starsEarned: 0, fed: 0, stickers: [], flowers: 0 },
  sessions: [],
  skills: {},
  gameStats: {},
  collection: { items: [], placed: [], stickers: [] },
  daily: { last: null, count: 0 },
  milestones: [],
  trials: [],
  freeJourneyDone: false,
  contentVersion: 2,
};

let cache = null;

export function getState() {
  if (cache) return cache;
  try {
    const raw = localStorage.getItem(KEY);
    cache = raw ? { ...DEFAULTS, ...JSON.parse(raw) } : { ...DEFAULTS };
    if ((cache.progress.starsEarned ?? 0) < cache.progress.stars) {
      cache.progress = { ...cache.progress, starsEarned: cache.progress.stars };
    }
  } catch {
    cache = { ...DEFAULTS };
  }
  return cache;
}

export function setState(patch) {
  cache = { ...getState(), ...patch };
  try { localStorage.setItem(KEY, JSON.stringify(cache)); } catch {}
  return cache;
}

export function updateProgress(patch) {
  return setState({ progress: { ...getState().progress, ...patch } });
}

export function addStars(n) {
  const p = getState().progress;
  return updateProgress({ stars: p.stars + n, starsEarned: (p.starsEarned || 0) + n });
}

export function recordSession(session) {
  const sessions = [session, ...getState().sessions].slice(0, 200);
  return setState({ sessions });
}

// Milestones are "first time" moments for the parent to read. Each one fires
// exactly once, ever — a repeated "first time" is worse than none at all.
// `key` is the identity of the moment; `data` is what the sentence needs.
export function addMilestoneOnce(key, data) {
  const list = getState().milestones || [];
  if (list.some((m) => m.key === key)) return null;
  const entry = { key, date: new Date().toISOString(), ...data };
  setState({ milestones: [entry, ...list].slice(0, 100) });
  return entry;
}

export function getMilestones() {
  return getState().milestones || [];
}

// How many distinct calendar days the child has actually played on.
// Derived from existing session history — no separate counter to drift.
export function distinctPlayDays() {
  const days = new Set();
  for (const s of getState().sessions || []) {
    if (!s?.date) continue;
    const d = new Date(s.date);
    if (Number.isNaN(d.getTime())) continue;
    days.add(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`);
  }
  return days.size;
}

export function recordSkill(skill, correct) {
  const skills = { ...getState().skills };
  const s = skills[skill] || { attempts: 0, correct: 0 };
  skills[skill] = { attempts: s.attempts + 1, correct: s.correct + (correct ? 1 : 0) };
  return setState({ skills });
}

export function resetProgress() {
  return setState({ progress: { ...DEFAULTS.progress }, sessions: [], skills: {}, gameStats: {},
    collection: { items: [], placed: [], stickers: [] }, freeJourneyDone: false });
}

export function earnSticker(gameId) {
  const c = getState().collection;
  if (c.stickers.includes(gameId)) return false;
  setState({ collection: { ...c, stickers: [...c.stickers, gameId] } });
  return true;
}

export function unlockGardenItem(itemId, cost) {
  const st = getState();
  if (st.progress.stars < cost || st.collection.items.includes(itemId)) return false;
  setState({
    progress: { ...st.progress, stars: st.progress.stars - cost },
    collection: { ...st.collection, items: [...st.collection.items, itemId], placed: [...st.collection.placed, itemId] },
  });
  return true;
}

export function deleteProfile() {
  cache = null;
  try { localStorage.removeItem(KEY); } catch {}
}

function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

export function isDailyRewardAvailable() {
  return (getState().daily || {}).last !== todayKey();
}

export function dailyRewardCount() {
  return (getState().daily || {}).count || 0;
}

export function claimDailyReward(stickerId) {
  const st = getState();
  const d = st.daily || { last: null, count: 0 };
  const stickers = st.collection.stickers.includes(stickerId)
    ? st.collection.stickers : [...st.collection.stickers, stickerId];
  return setState({
    daily: { last: todayKey(), count: (d.count || 0) + 1 },
    collection: { ...st.collection, stickers },
  });
}

export function hasTriedGame(gameId) {
  return (getState().trials || []).includes(gameId);
}

export function markGameTried(gameId) {
  const st = getState();
  if ((st.trials || []).includes(gameId)) return st;
  return setState({ trials: [...(st.trials || []), gameId] });
}
